import {useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import {AlertCircle, Check, Info, Loader2, Mic, MicOff, Phone, Video, VideoOff, X} from 'lucide-react';
import type {CallIntent} from '@phonehq/rtc-core';
import {CallAvatar} from '../components/CallAvatar';
import type {CallIdentity} from '../model/types';
import {prepMessages as m} from './prepMessages';
import {CallLayer} from '../components/CallLayer';

export interface PrepPerson {
    readonly id: string;
    readonly identity: CallIdentity;
    readonly presence: string;
    readonly selected: boolean;
    readonly selectable: boolean;
}

export interface PrepBanner {
    readonly tone: 'warning' | 'danger' | 'info';
    readonly title: string;
    readonly body: string;
    readonly action?: {readonly label: string; readonly onClick: () => void};
    readonly testId?: string;
}

export interface PrepDevice {
    readonly deviceId: string;
    readonly label: string;
}

export interface PrepDeviceField {
    readonly enabled: boolean;
    readonly onToggle: () => void;
    readonly devices: readonly PrepDevice[];
    readonly value: string;
    readonly onChange: (deviceId: string) => void;
    readonly unavailableLabel?: string;
    readonly testId: string;
}

interface PrepDialogProps {
    readonly subtitle: string;
    readonly intent: CallIntent;
    readonly videoAvailable: boolean;
    readonly onIntent: (intent: CallIntent) => void;
    readonly people: readonly PrepPerson[];
    readonly onTogglePerson?: (id: string) => void;
    readonly onToggleAll?: () => void;
    /** The "Add people from Team" search, shown above the list. */
    readonly addPeople?: ReactNode;
    readonly microphone: PrepDeviceField;
    readonly camera?: PrepDeviceField;
    readonly preview?: ReactNode;
    readonly selfIdentity: CallIdentity;
    readonly banner?: PrepBanner;
    readonly footerNote?: string;
    readonly primary: {readonly label: string; readonly busy: boolean; readonly disabled: boolean; readonly onClick: () => void};
    readonly onCancel: () => void;
}

const VISIBLE_PEOPLE = 5;

const BANNER_TONES: Record<PrepBanner['tone'], string> = {
    warning: 'bg-[var(--phq-call-warning-tint)] text-[var(--phq-call-warning-ink)]',
    danger: 'bg-[var(--phq-call-danger-tint)] text-[var(--phq-call-danger)]',
    info: 'bg-[var(--phq-call-brand-tint-subtle)] text-[var(--phq-call-brand-ink)]',
};

const Segment = ({active, icon, label, onClick, testId}: {active: boolean; icon: ReactNode; label: string; onClick: () => void; testId: string}) => (
    <button
        type="button"
        role="radio"
        aria-checked={active}
        data-testid={testId}
        onClick={onClick}
        className={`inline-flex h-8 flex-1 items-center justify-center gap-2 rounded-lg text-sm ${active ? 'bg-[var(--phq-call-surface)] font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.08)]' : 'font-medium text-[var(--phq-call-ink-muted)]'}`}
    >
        {icon}
        {label}
    </button>
);

const DeviceRow = ({label, field, icon, offIcon, onLabel, offLabel}: {label: string; field: PrepDeviceField; icon: ReactNode; offIcon: ReactNode; onLabel: string; offLabel: string}) => {
    const id = useId();
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={id} className="text-sm font-medium">{label}</label>
            <div className="flex gap-2">
                <button
                    type="button"
                    aria-pressed={!field.enabled && !field.unavailableLabel}
                    aria-label={field.unavailableLabel ?? (field.enabled ? offLabel : onLabel)}
                    data-testid={field.testId}
                    onClick={field.onToggle}
                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-solid ${field.enabled ? 'border-[var(--phq-call-line)] bg-[var(--phq-call-surface)]' : 'border-transparent bg-[var(--phq-call-danger-tint)] text-[var(--phq-call-danger)]'}`}
                >
                    {field.enabled ? icon : offIcon}
                </button>
                <select
                    id={id}
                    value={field.value}
                    disabled={!!field.unavailableLabel}
                    onChange={event => field.onChange(event.target.value)}
                    className="h-10 min-w-0 flex-1 rounded-[10px] border border-solid border-[var(--phq-call-line)] bg-[var(--phq-call-surface)] px-3 text-sm disabled:text-[var(--phq-call-ink-muted)]"
                >
                    {field.unavailableLabel ? (
                        <option value="">{field.unavailableLabel}</option>
                    ) : (
                        field.devices.map(device => <option key={device.deviceId || 'default'} value={device.deviceId}>{device.label}</option>)
                    )}
                </select>
            </div>
        </div>
    );
};

const PersonSummary = ({person}: {person: PrepPerson}) => (
    <>
        <CallAvatar identity={person.identity} size={32}/>
        <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{person.identity.name ?? person.id}</span>
            <span className="block truncate text-xs text-[var(--phq-call-ink-muted)]">{person.presence}</span>
        </span>
    </>
);

export const PrepDialog = (props: PrepDialogProps) => {
    const intl = useIntl();
    const titleId = useId();
    const panel = useRef<HTMLDivElement>(null);
    const {people, banner, primary} = props;
    const selected = people.filter(person => person.selected);
    // D-CHAT-06/S12–S14: the list shows five people; the rest open in place
    // from "+ N more selected" (or "Show N more" when none of them is picked).
    const [expanded, setExpanded] = useState(false);
    const visible = expanded ? people : people.slice(0, VISIBLE_PEOPLE);
    const hidden = people.slice(VISIBLE_PEOPLE);
    const hiddenSelected = hidden.filter(person => person.selected).length;
    const allSelected = people.filter(person => person.selectable).every(person => person.selected);

    useEffect(() => {
        panel.current?.focus();
    }, []);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            props.onCancel();
        }
    };

    return createPortal(
        <CallLayer>{
        <div className="phq-call-light fixed inset-0 z-[80] flex items-center justify-center bg-[var(--phq-call-scrim)] p-4" onKeyDown={onKeyDown}>
            <div
                ref={panel}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                data-testid="group-call-lobby"
                className="phq-call-enter flex max-h-[min(780px,calc(100vh-32px))] w-full max-w-[560px] flex-col overflow-hidden rounded-2xl bg-[var(--phq-call-surface)] phq-call-shadow-dialog outline-none"
            >
                <header className="flex items-start justify-between gap-3 px-6 pb-3 pt-5">
                    <div className="min-w-0">
                        <h2 id={titleId} className="text-lg font-semibold">{intl.formatMessage(m.title)}</h2>
                        <p className="truncate text-sm text-[var(--phq-call-ink-muted)]">{props.subtitle}</p>
                    </div>
                    <button
                        type="button"
                        aria-label={intl.formatMessage(m.close)}
                        onClick={props.onCancel}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-surface-muted)]"
                    >
                        <X size={18} aria-hidden="true"/>
                    </button>
                </header>
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-4">
                    <div role="radiogroup" aria-label={intl.formatMessage(m.callType)} className="flex gap-0.5 rounded-[10px] bg-[var(--phq-call-surface-muted)] p-0.5 shadow-[inset_0_0_0_1px_var(--phq-call-line)]">
                        <Segment active={props.intent === 'audio'} icon={<Phone size={16} aria-hidden="true"/>} label={intl.formatMessage(m.voice)} testId="prep-voice" onClick={() => props.onIntent('audio')}/>
                        {props.videoAvailable && (
                            <Segment active={props.intent === 'video'} icon={<Video size={16} aria-hidden="true"/>} label={intl.formatMessage(m.video)} testId="prep-video" onClick={() => props.onIntent('video')}/>
                        )}
                    </div>
                    {banner && (
                        <div role={banner.tone === 'info' ? 'status' : 'alert'} data-testid={banner.testId} className={`flex gap-2 rounded-xl px-3 py-2.5 text-[13px] ${BANNER_TONES[banner.tone]}`}>
                            {banner.tone === 'info' ? <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true"/> : <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true"/>}
                            <div className="min-w-0">
                                <p><span className="font-semibold">{banner.title}</span> {banner.body}</p>
                                {banner.action && (
                                    <button type="button" onClick={banner.action.onClick} className="mt-1 font-semibold underline">{banner.action.label}</button>
                                )}
                            </div>
                        </div>
                    )}
                    {people.length > 0 && (
                        <section>
                            <div className="flex items-center justify-between pb-1">
                                <h3 className="text-sm font-semibold">{intl.formatMessage(m.peopleSelected, {count: selected.length})}</h3>
                                {props.onToggleAll && (
                                    <button type="button" data-testid="prep-select-all" onClick={props.onToggleAll} className="text-[13px] font-semibold text-[var(--phq-call-brand-ink)]">
                                        {intl.formatMessage(allSelected ? m.clearAll : m.selectAll)}
                                    </button>
                                )}
                            </div>
                            {props.addPeople}
                            <ul className="flex flex-col" data-testid="group-call-lobby-participants">
                                {visible.map(person => (
                                    <li key={person.id} className="flex h-12 items-center">
                                        {props.onTogglePerson && person.selectable ? (
                                            <button
                                                type="button"
                                                role="checkbox"
                                                aria-checked={person.selected}
                                                aria-label={person.identity.name ?? person.id}
                                                onClick={() => props.onTogglePerson?.(person.id)}
                                                className="flex h-full w-full min-w-0 cursor-pointer items-center gap-3 rounded-lg text-left"
                                            >
                                                <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-solid ${person.selected ? 'border-[var(--phq-call-brand)] bg-[var(--phq-call-brand)] text-white' : 'border-[var(--phq-call-line)] bg-[var(--phq-call-surface)]'}`}>
                                                    {person.selected && <Check size={14} aria-hidden="true"/>}
                                                </span>
                                                <PersonSummary person={person}/>
                                            </button>
                                        ) : (
                                            <div className="flex w-full min-w-0 items-center gap-3">
                                                <span className="w-5 shrink-0"/>
                                                <PersonSummary person={person}/>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {hidden.length > 0 && (
                                <button type="button" data-testid="prep-more" aria-expanded={expanded}
                                        onClick={() => setExpanded(open => !open)}
                                        className="pt-1 text-[13px] font-semibold text-[var(--phq-call-brand-ink)] hover:underline">
                                    {expanded
                                        ? intl.formatMessage(m.showLess)
                                        : hiddenSelected > 0
                                            ? intl.formatMessage(m.more, {count: hiddenSelected})
                                            : intl.formatMessage(m.showMore, {count: hidden.length})}
                                </button>
                            )}
                        </section>
                    )}
                    <DeviceRow
                        label={intl.formatMessage(m.microphone)}
                        field={props.microphone}
                        icon={<Mic size={18} aria-hidden="true"/>}
                        offIcon={<MicOff size={18} aria-hidden="true"/>}
                        onLabel={intl.formatMessage(m.turnMicrophoneOn)}
                        offLabel={intl.formatMessage(m.turnMicrophoneOff)}
                    />
                    {props.camera && (
                        <DeviceRow
                            label={intl.formatMessage(m.camera)}
                            field={props.camera}
                            icon={<Video size={18} aria-hidden="true"/>}
                            offIcon={<VideoOff size={18} aria-hidden="true"/>}
                            onLabel={intl.formatMessage(m.turnCameraOn)}
                            offLabel={intl.formatMessage(m.turnCameraOff)}
                        />
                    )}
                    {props.camera && (
                        <div role="img" aria-label={intl.formatMessage(m.cameraPreview)} className="relative h-[168px] overflow-hidden rounded-xl bg-[var(--phq-call-surface-muted)]">
                            {props.camera.unavailableLabel ? (
                                <div className="flex h-full items-center justify-center text-sm font-medium text-[var(--phq-call-ink-muted)]">
                                    {props.camera.unavailableLabel}
                                </div>
                            ) : (
                                <>
                                    {props.preview ?? (
                                        <div className="flex h-full items-center justify-center">
                                            <CallAvatar identity={props.selfIdentity} size={64}/>
                                        </div>
                                    )}
                                    <span className="absolute bottom-2 left-2 inline-flex h-6 items-center rounded-lg bg-[rgba(9,9,11,.6)] px-2 text-xs font-semibold text-white">
                                        {intl.formatMessage(props.preview ? m.youCameraOn : m.youCameraOff)}
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                </div>
                <footer className="flex items-center justify-between gap-3 border-t border-solid border-[var(--phq-call-line)] px-6 py-4">
                    <span className="text-[13px] text-[var(--phq-call-ink-muted)]">{props.footerNote}</span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            data-testid="group-call-lobby-cancel"
                            onClick={props.onCancel}
                            className="inline-flex h-10 items-center rounded-[10px] bg-[var(--phq-call-surface-muted)] px-4 text-sm font-semibold"
                        >
                            {intl.formatMessage(m.cancel)}
                        </button>
                        <button
                            type="button"
                            data-testid="group-call-lobby-join"
                            disabled={primary.disabled || primary.busy}
                            aria-busy={primary.busy || undefined}
                            onClick={primary.onClick}
                            className="inline-flex h-10 min-w-[96px] items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-4 text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                        >
                            {primary.busy && <Loader2 size={16} className="animate-spin" aria-hidden="true"/>}
                            {primary.busy ? intl.formatMessage(m.starting) : primary.label}
                        </button>
                    </div>
                </footer>
            </div>
        </div>}</CallLayer>,
        document.body,
    );
};
